import axios from 'axios'

function getCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[$()*+.?[\\\]^{|}-]/g, '\\$&') + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : ''
}

// ✅ must match YOUR CsrfDoubleSubmit cookie name
const CSRF_COOKIE = '_webapp_csrf' // <-- change to whatever your plug sets

axios.defaults.withCredentials = true

let refreshPromise = null
const idempotencyKeys = new Map()
const IDEMPOTENCY_WINDOW_MS = 30_000
const promptedChangeReviewIds = new Set()
const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete'])

function requestFingerprint(config, method) {
  let body = ''

  if (config.data instanceof FormData) {
    body = Array.from(config.data.entries()).map(([key, value]) => {
      if (typeof File !== 'undefined' && value instanceof File) {
        return [key, value.name, value.size, value.lastModified]
      }
      return [key, String(value)]
    })
  } else if (typeof config.data === 'string') {
    body = config.data
  } else if (config.data != null) {
    body = JSON.stringify(config.data)
  }

  return JSON.stringify([method, config.url || '', config.params || null, body])
}

function idempotencyKeyFor(config, method) {
  const now = Date.now()
  const fingerprint = requestFingerprint(config, method)
  const existing = idempotencyKeys.get(fingerprint)

  if (existing && existing.expiresAt > now) return existing.key

  const key = globalThis.crypto?.randomUUID?.() ||
    `${now}-${Math.random().toString(16).slice(2)}`

  idempotencyKeys.set(fingerprint, {
    key,
    expiresAt: now + IDEMPOTENCY_WINDOW_MS
  })

  for (const [storedFingerprint, value] of idempotencyKeys) {
    if (value.expiresAt <= now) idempotencyKeys.delete(storedFingerprint)
  }

  return key
}

axios.interceptors.request.use((config) => {
  config.withCredentials = true

  const method = (config.method || 'get').toLowerCase()
  if (MUTATION_METHODS.has(method)) {
    const token = getCookie(CSRF_COOKIE)
    if (token) {
      config.headers = config.headers || {}
      config.headers['x-csrf-token'] = token
    }

    config.headers = config.headers || {}
    const hasIdempotencyKey = typeof config.headers.has === 'function'
      ? config.headers.has('Idempotency-Key')
      : Boolean(config.headers['Idempotency-Key'])

    if (!hasIdempotencyKey) {
      config.headers['Idempotency-Key'] = idempotencyKeyFor(config, method)
    }
  }

  return config
})

axios.interceptors.response.use((response) => {
  const method = String(response?.config?.method || 'get').toLowerCase()
  const reviewId = response?.data?.change_review_queue_id

  if (
    typeof window !== 'undefined' &&
    MUTATION_METHODS.has(method) &&
    response?.data?.pending_review &&
    reviewId &&
    !promptedChangeReviewIds.has(String(reviewId))
  ) {
    promptedChangeReviewIds.add(String(reviewId))
    window.dispatchEvent(new CustomEvent('change-review-submitted', {
      detail: response.data
    }))
  }

  return response
}, async (error) => {
  const request = error?.config
  const status = error?.response?.status
  const url = String(request?.url || '')

  if (
    status === 409 &&
    error?.response?.data?.retryable &&
    request &&
    (request._idempotencyRetry || 0) < 50
  ) {
    request._idempotencyRetry = (request._idempotencyRetry || 0) + 1
    await new Promise(resolve => setTimeout(resolve, 200))
    return axios.request(request)
  }

  if (
    status !== 401 ||
    !request ||
    request._sessionRetry ||
    request._skipSessionRefresh ||
    url.startsWith('/api/auth/')
  ) {
    return Promise.reject(error)
  }

  request._sessionRetry = true

  if (!refreshPromise) {
    refreshPromise = axios.post(
      '/api/auth/refresh',
      {},
      {
        withCredentials: true,
        _skipSessionRefresh: true
      }
    ).finally(() => {
      refreshPromise = null
    })
  }

  try {
    await refreshPromise
    return axios.request(request)
  } catch {
    return Promise.reject(error)
  }
})

export default axios
